import {
  FindingsOrder,
  FindingsOrderField,
  FindingType,
  OrderDirection,
  ScoreRating,
  ScoreStateFilter,
  useGetAdvisoriesQuery,
} from "~/operations";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { usePaginate } from "~/components/pagination/usePaginate";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { PaginateProps } from "~/components/pagination/paginate";

type UseAssetSoftwareAdvisories = {
  assetMrn: string;
  softwareMrn: string;
};

type UseAssetSoftwareAdvisoriesReturn = {
  advisories: AdvisoryFindingsNode[];
  loading: boolean;
  totalCount: number;
  paginationProps: PaginateProps;
  assetId: string;
  hasError: boolean;
  hasAdvisories: boolean;
};

export function useAssetSoftwareAdvisories({
  assetMrn,
  softwareMrn,
}: UseAssetSoftwareAdvisories): UseAssetSoftwareAdvisoriesReturn {
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [
      FindingsOrderField.RiskValue,
      FindingsOrderField.LastUpdated,
      FindingsOrderField.Mrn,
    ],
  });

  const {
    data: softwareAdvisoriesData,
    error: softwareAdvisoriesError,
    loading: softwareAdvisoriesLoading,
  } = useGetAdvisoriesQuery({
    variables: {
      scopeMrn: assetMrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        types: [FindingType.Advisory],
        state: ScoreStateFilter.Open,
        rating: [
          ScoreRating.Low,
          ScoreRating.Medium,
          ScoreRating.High,
          ScoreRating.Critical,
        ],
        softwareFindingMrn: softwareMrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const advisoryFindingsUnion =
    softwareAdvisoriesData?.findings?.__typename === "FindingsConnection"
      ? softwareAdvisoriesData?.findings
      : undefined;

  const paginationProps = paginate.pageProps(advisoryFindingsUnion?.pageInfo);

  const advisoryFindings: AdvisoryFindingsNode[] = (
    advisoryFindingsUnion?.edges || []
  ).flatMap((e) => (e?.node?.__typename === "AdvisoryFinding" ? e.node : []));

  const hasAdvisories = (advisoryFindings?.length || 0) > 0;
  const assetId = assetMrn.split("/").pop() || "";

  return {
    advisories: advisoryFindings,
    loading: softwareAdvisoriesLoading,
    totalCount: advisoryFindingsUnion?.totalCount ?? 0,
    paginationProps,
    hasError: !!softwareAdvisoriesError,
    hasAdvisories,
    assetId,
  };
}
